import type { FactorEntry } from "../services/productFactorsService";
import { factorsToDailyReturns } from "./riskMetrics";
import { filterCompletedMarketDayFactors } from "./strategyAnalyzerMarketCalendar";
import {
  filterFactorsForAnalyzerWindow,
  type AnalyzerWindow,
} from "./strategyAnalyzerPeriods";

export interface DraftFactorProductInput {
  key: string;
  name?: string;
  weight: number;
  factors: FactorEntry[];
}

export type StrategySimulationWeightMode = "rebalanced" | "drifted";

export interface DraftProductDailyContribution {
  key: string;
  name: string;
  weight: number;
  endWeight: number | null;
  productReturnPct: number;
  contributionPct: number;
}

export interface DraftStrategyDailyBreakdown {
  date: string;
  strategyReturnPct: number;
  products: DraftProductDailyContribution[];
}

function toUtcDay(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value.slice(0, 10);
  return parsed.toISOString().slice(0, 10);
}

/**
 * Builds a simulated strategy factor series from product factor history.
 * Rebalanced mode reapplies signed target weights each day. Drifted mode lets
 * sleeve values move with product returns while cash stays flat.
 */
export function buildWeightedDraftStrategyFactors(
  products: DraftFactorProductInput[],
  mode: StrategySimulationWeightMode = "rebalanced"
): FactorEntry[] {
  return buildWeightedDraftStrategyDailyBreakdowns(products, mode).map((breakdown) => ({
    t: `${breakdown.date}T00:00:00.000Z`,
    f: 1 + breakdown.strategyReturnPct / 100,
  }));
}

export function buildWeightedDraftStrategyDailyBreakdowns(
  products: DraftFactorProductInput[],
  mode: StrategySimulationWeightMode = "rebalanced"
): DraftStrategyDailyBreakdown[] {
  const activeProducts = products.filter((product) => product.weight !== 0);
  if (activeProducts.length === 0) return [];

  const productReturnMaps = activeProducts.map((product) => {
    const returns = factorsToDailyReturns(product.factors);
    return {
      key: product.key,
      name: product.name ?? product.key,
      weight: product.weight,
      returnsByDay: new Map(returns.map((entry) => [toUtcDay(entry.t), entry.r])),
    };
  });

  if (productReturnMaps.some((product) => product.returnsByDay.size === 0)) return [];

  const commonDays = Array.from(productReturnMaps[0].returnsByDay.keys()).filter((day) =>
    productReturnMaps.every((product) => product.returnsByDay.has(day))
  );

  commonDays.sort();

  if (mode === "drifted") {
    return buildDriftedDailyBreakdowns(productReturnMaps, commonDays);
  }

  return commonDays.map((day) => {
    const productContributions = productReturnMaps.map((product) => {
      const productReturn = product.returnsByDay.get(day) ?? 0;
      return {
        key: product.key,
        name: product.name,
        weight: product.weight,
        endWeight: product.weight,
        productReturnPct: productReturn * 100,
        contributionPct: product.weight * productReturn,
      };
    });

    return {
      date: day,
      strategyReturnPct: productContributions.reduce(
        (sum, product) => sum + product.contributionPct,
        0
      ),
      products: productContributions,
    };
  });
}

export function buildAnalyzerWindowDraftStrategyFactors(
  products: DraftFactorProductInput[],
  mode: StrategySimulationWeightMode,
  window: AnalyzerWindow | null
): FactorEntry[] {
  return buildAnalyzerWindowDraftStrategyDailyBreakdowns(products, mode, window).map((breakdown) => ({
    t: `${breakdown.date}T00:00:00.000Z`,
    f: 1 + breakdown.strategyReturnPct / 100,
  }));
}

export function buildAnalyzerWindowDraftStrategyDailyBreakdowns(
  products: DraftFactorProductInput[],
  mode: StrategySimulationWeightMode,
  window: AnalyzerWindow | null
): DraftStrategyDailyBreakdown[] {
  if (mode !== "drifted") {
    return buildWeightedDraftStrategyDailyBreakdowns(products, mode);
  }

  return buildWeightedDraftStrategyDailyBreakdowns(
    products.map((product) => ({
      ...product,
      factors: filterFactorsForAnalyzerWindow(
        compactFactorsToDailyFactors(filterCompletedMarketDayFactors(product.factors)),
        window
      ),
    })),
    mode
  );
}

function compactFactorsToDailyFactors(factors: FactorEntry[]): FactorEntry[] {
  return factorsToDailyReturns(factors).map((point) => ({
    t: point.t,
    f: 1 + point.r,
  }));
}

function buildDriftedDailyBreakdowns(
  products: Array<{ key: string; name: string; weight: number; returnsByDay: Map<string, number> }>,
  commonDays: string[]
): DraftStrategyDailyBreakdown[] {
  let sleeveValues = products.map((product) => product.weight / 100);
  const cashValue = 1 - sleeveValues.reduce((sum, value) => sum + value, 0);
  let portfolioValue = cashValue + sleeveValues.reduce((sum, value) => sum + value, 0);

  return commonDays.map((day) => {
    const startingPortfolioValue = portfolioValue;
    const productReturns = products.map((product) => product.returnsByDay.get(day) ?? 0);
    const nextSleeveValues = sleeveValues.map((value, index) => value * (1 + productReturns[index]));
    const nextPortfolioValue = cashValue + nextSleeveValues.reduce((sum, value) => sum + value, 0);
    const productContributions = products.map((product, index) => {
      const productReturn = product.returnsByDay.get(day) ?? 0;
      const weight =
        startingPortfolioValue === 0
          ? 0
          : (sleeveValues[index] / startingPortfolioValue) * 100;
      const endWeight =
        Number.isFinite(nextPortfolioValue) && Math.abs(nextPortfolioValue) > Number.EPSILON
          ? (nextSleeveValues[index] / nextPortfolioValue) * 100
          : null;
      const contributionPct =
        startingPortfolioValue === 0
          ? 0
          : (sleeveValues[index] * productReturn / startingPortfolioValue) * 100;
      return {
        key: product.key,
        name: product.name,
        weight,
        endWeight,
        productReturnPct: productReturn * 100,
        contributionPct,
      };
    });

    const dailyFactor = portfolioValue === 0 ? 1 : nextPortfolioValue / portfolioValue;
    sleeveValues = nextSleeveValues;
    portfolioValue = nextPortfolioValue;

    return {
      date: day,
      strategyReturnPct: Number.isFinite(dailyFactor) ? (dailyFactor - 1) * 100 : 0,
      products: productContributions,
    };
  });
}
